import { useContext, useMemo } from 'react';
import { OkButton } from 'src/components/Buttons/OkButton';
import { useContent } from 'src/contexts/ContentContext';
import { ModalContext } from 'src/contexts/ModalContext';
import { Stack } from 'src/core/ui';
import type { ModalProps } from 'src/modals/Modal';
import { ContentId } from 'src/utils/constants/contentId';
import { formatCurrency } from 'src/utils/numberFormatter';
import { MarketplacePaymentState, SaleDetails } from 'src/WebApiController';

import * as styles from './MarketplacePaymentAdyenAction.css';
import { MarketplacePaymentDialog } from './MarketplacePaymentDialog';

type ModalConfig = Omit<ModalProps, 'fullscreen'>;

type MarketplacePaymentAdyenActionProps = {
  sale: SaleDetails;
};

export const MarketplacePaymentAdyenAction = ({
  sale,
}: MarketplacePaymentAdyenActionProps) => {
  const { setModal, currentModalConfig } = useContext(ModalContext);
  const saleDetailsConfig = currentModalConfig as ModalConfig | null;
  const remainingText = useContent(ContentId.Remaining);

  const remainingAmount = useMemo(
    () =>
      (sale.mkpLines ?? [])
        .filter((l) => l.state === MarketplacePaymentState.Unpaid)
        .reduce((sum, l) => sum + l.amt.amt, 0),
    [sale.mkpLines]
  );

  const currencyCode = sale.mkpLines?.[0]?.amt.currency ?? 'USD';

  if (remainingAmount <= 0) {
    return null;
  }

  return (
    <Stack alignItems="center" gap="m" className={styles.container}>
      <span className={styles.remainingLabel}>
        {`${remainingText}: ${formatCurrency(remainingAmount, currencyCode)}`}
      </span>
      <OkButton
        textContentId={ContentId.PayNow}
        variant="outlineBrand"
        size="md"
        onClick={() =>
          setModal({
            children: (
              <MarketplacePaymentDialog
                sale={sale}
                cancelTo={saleDetailsConfig}
              />
            ),
          })
        }
      />
    </Stack>
  );
};
